import * as React from "react";
import { Link } from "react-router-dom";
import { Device, WebPortalClient } from "webportalclient";
import { MatchSortHeader, Option } from "../../Components/MatchSortHeader";
import { useQuery } from "../../Components/Query";
import { Column, Table } from "../../Components/QueryTable";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useReferenceContextProvider } from "../../Core/ReferenceContextProvider";
import { useSecurityContext } from "../../Core/SecurityContext";
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { ApiClients, ApiGetClientById, ApiPutClientById } from "../../misc/api/AccountServer/AccountApi";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { QueryCondition } from "../../misc/api/apiTypes";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import AsyncActionButton from "../../misc/components/AsyncActionButton";
import { Authorization } from "../../misc/components/Authorization";
import { HamburgerMenu } from "../../misc/components/HamburgerMenu";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import Modal from "../../misc/components/modal";
import { uniqBy } from "../../misc/ArrayFunctions";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { ImportDevices } from "./ImportDevices";
import { UpdateDevicesVersions } from "./UpdateDevicesVersions";

interface DeviceHamburgerMenuProps {
    device: Device;
}

declare const rootUrl: string;

export const DevicesTable = () => {
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const { data, applyFilters, refresh } = useQuery();
    const { deviceTypes } = useReferenceContextProvider();
    const client = useWebPortalApiClient();
    const [serialNumbers, setSerialNumbers] = React.useState<Option[]>([]);
    const [studies, setStudies] = React.useState<Option[]>([]);
    const [sites, setSites] = React.useState<Option[]>([]);
    const [spirotracPackageVersions, setSpirotracPackageVersions] = React.useState<Option[]>([]);
    const [subjects, setSubjects] = React.useState<Option[]>([]);
    const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
    const [deleteModalId, setDeleteModalId] = React.useState<string>();
    const [error, setError] = React.useState<string>();
    const [importSucceeded, setImportSucceeded] = React.useState<boolean>(false);

    const [importDevicesVisible, setImportDevicesVisible] = React.useState(false);
    const [updateSpirotracVersionsVisible, setUpdateSpirotracVersionsVisible] = React.useState(false);

    React.useEffect(() => {
        const getFilters = async () => {

            const devices = await client.client.getDevices("serialNumber,spirotracPackageVersion", undefined);
            const serialNumberOptions = uniqBy(devices.value, (a, b) => a.serialNumber === b.serialNumber).filter(o => o.serialNumber).map(d => ({
                value: d.serialNumber,
                label: d.serialNumber,
            }));

            setSerialNumbers(serialNumberOptions);

            const spirotracPackageVersionOptions = uniqBy(devices.value, (a, b) => a.spirotracPackageVersion === b.spirotracPackageVersion).filter(o => o.spirotracPackageVersion).map(d => ({
                value: d.spirotracPackageVersion,
                label: d.spirotracPackageVersion,
            }));
            setSpirotracPackageVersions(spirotracPackageVersionOptions);

            const studies = await client.client.getStudies("id,name", undefined, "name");
            const studyOptions = uniqBy(studies.value.map(s => ({
                value: s.id,
                label: s.name,
            })), (a,b) => a.value === b.value);

            setStudies(studyOptions);

            const sites = await client.client.getSites("id,number", undefined, "number");
            const siteOptions = uniqBy(sites.value.map(s => ({
                value: s.id,
                label: s.number,
            })), (a, b) => a.value === b.value);

            setSites(siteOptions);

            const subjects = await client.client.getSubjects("id,number", undefined, "number", "site");
            const subjectOptions = uniqBy(subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            })), (a, b) => a.value === b.value);

            setSubjects(subjectOptions);
        };

        getFilters();
    }, []);

    const handleClearFilters = () => applyFilters([]);

    const DeviceHamburgerMenu = ({ device }: DeviceHamburgerMenuProps) => {
        return (<HamburgerMenu>
            <li>
                <Link to={`/deviceadministration/edit/${device.id}`}>Edit Device</Link>
            </li>
            <li>
                <a onClick={() => { setDeleteModalVisible(true); setDeleteModalId(device.id); }}>Delete Device</a>
            </li>
        </HamburgerMenu>);
    }

    const onDeleteModalClose = () => {
        setDeleteModalVisible(false);
    }

    const onDeleteSubmit = async () => {
        try {
            setError(undefined);
            const serialNumber = data.find(d => d.id === deleteModalId).serialNumber;

            const clientResult = await ApiClients({
                Filters: [{
                    Member: "serial",
                    Condition: QueryCondition.Equal,
                    Values: [serialNumber]
                }]
            });

            await client.client.deleteDevice(deleteModalId);

            const remainingDevicesForOtherStudies = await client.client.getDevices(undefined, `serialNumber eq '${serialNumber}'`);

            if (remainingDevicesForOtherStudies.value.length === 0 && clientResult.data[0].portal === rootUrl.replace(/\/$/, "")) {
                const patch = {
                    ...clientResult.data[0],
                    portal: null,
                }
                await ApiPutClientById(patch.id, patch);
            }

            setDeleteModalVisible(false);
            applyFilters([]);
        } catch (ex) {
            setError("Unable to delete device allocation from Vitalograph Account");
        }
    }

    const onImportClose = () => {
        setImportDevicesVisible(false);
        refresh();
    }

    const onUpdateSpirotracVersionsClose = () => {
        setUpdateSpirotracVersionsVisible(false);
        refresh();
    }

    if (!deviceTypes || deviceTypes.length === 0)
        return <LoadingWithMessage message="Loading Device Administration..." />;

    return (
        <>
            <Alert show={!!error} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>
            <Alert show={importSucceeded} category={AlertCategoryEnum.Success}>
                Devices imported successfully
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Device Administration</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.AddDevices}>
                            <Link id="createNew" className="btn btn-primary" to="/deviceadministration/create">
                                Create New
                            </Link>
                        </Authorization>&nbsp;
                        <button id="clearAllFilters" className="btn btn-primary" onClick={handleClearFilters}>
                            Clear All Filters
                        </button>&nbsp;
                        <button id="clearAllFilters" className="btn btn-primary" onClick={() => setUpdateSpirotracVersionsVisible(true)}>
                            Update Package Versions
                        </button>&nbsp;
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionIdsAny={[PermissionEnum.ImportDevices]}>
                            <button className="btn btn-primary btn-outline" onClick={() => setImportDevicesVisible(true)}>Import Devices</button>
                        </Authorization>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div className="table-responsive" data-automationid="DeviceAdministrationTable">
                                <Table data={data}>
                                    <Column field="deviceType" header={<MatchSortHeader title="Device Type" field="deviceType" options={deviceTypes.map(s => ({
                                        value: s.id,
                                        label: s.name,
                                    }))} sortField="deviceType" />} formatter={f => deviceTypes.find(x => x.id === f).name} />
                                    <Column field="serialNumber" header={<MatchSortHeader title="Serial Number" field="serialNumber" options={serialNumbers} sortField="serialNumber" />} formatter={f => f} />
                                    <Column field="studyId" header={<MatchSortHeader title="Study" field="studyId" options={studies} sortField="studyId" />} formatter={f => studies.find(s => s.value === f)?.label} />
                                    <Column field="allocatedSiteId" header={<MatchSortHeader title="Allocated Site" field="allocatedSiteId" options={sites} sortField="allocatedSiteId" />} formatter={f => sites.find(s => s.value === f)?.label} />
                                    <Column field="allocatedSubjectId" header={<MatchSortHeader title="Allocated Subject" field="allocatedSubjectId" options={subjects} sortField="allocatedSubjectId" />} formatter={f => subjects.find(s => s.value === f)?.label} />
                                    <Column field="spirotracPackageVersion" header={<MatchSortHeader title="Package Version" field="spirotracPackageVersion" options={spirotracPackageVersions} sortField="spirotracPackageVersion" />} formatter={f => f} />
                                    <Column field="" formatter={(s) => <DeviceHamburgerMenu device={s} />} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {deleteModalVisible ?
                <Modal show={true} closeModalHandler={onDeleteModalClose} header="Delete Device">
                    <ErrorBoundary>
                        <div className="modal-body">
                            <Alert show={true} category={AlertCategoryEnum.Warning}>
                                Are you sure you want to delete the device &apos;{data.find(d => d.id === deleteModalId).serialNumber}&apos;?
                            </Alert>
                        </div>
                        <div className="modal-footer">
                            <AsyncActionButton
                                className="btn btn-danger"
                                type="submit"
                                isSubmitting={false}
                                onClick={onDeleteSubmit}
                            >
                                Delete
                            </AsyncActionButton>
                            <button className="btn btn-outline btn-primary" onClick={onDeleteModalClose}>Cancel</button>
                        </div>
                    </ErrorBoundary>
                </Modal>
                : null}
            {importDevicesVisible ? <ImportDevices setImportSucceeded={setImportSucceeded} onClose={onImportClose} /> : null}
            
            {updateSpirotracVersionsVisible ? <UpdateDevicesVersions onClose={onUpdateSpirotracVersionsClose} /> : null}
        </>
    );
}
